import {
  Close,
  Minimize,
  OpenInFull,
  OpenInNew as OpenInNewIcon,
  Refresh,
  StarBorder as StarBorderIcon,
  Star as StarIcon,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { WorkItemList, WorkItemTypeEnum } from "../../../store/services/timelineApiRTK";
import { WorkItemFavoriteTypeEnum } from "../../../store/services/workItemService";
import {
  getTeamColor,
  getWorkItemIcon,
  getWorkItemTypeName,
} from "../../../utils/workItemUtils";
import { useNavigate } from "react-router-dom";

interface ActivityPanelHeaderProps {
  activity: WorkItemList | undefined;
  activityId: number;
  isRefreshing: boolean;
  minimized: boolean;
  onToggleMinimize: () => void;
  onClose: () => void;
  onToggleFavorite: () => void;
}

const ActivityPanelHeader = ({
  activity,
  activityId,
  isRefreshing,
  minimized,
  onToggleMinimize,
  onClose,
  onToggleFavorite,
}: ActivityPanelHeaderProps) => {
  const navigate = useNavigate();
  const teamColor = activity ? getTeamColor(activity.Team?.Id) : undefined;

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        px: 2,
        py: 1,
        background: teamColor
          ? `linear-gradient(135deg, ${teamColor} 0%, ${teamColor}dd 100%)`
          : "primary.main",
        color: "white",
        borderRadius: "12px 12px 0 0",
        cursor: "pointer",
        userSelect: "none",
      }}
      onClick={minimized ? onToggleMinimize : undefined}
    >
      <Box display="flex" alignItems="center" gap={1.5} minWidth={0}>
        <Avatar
          sx={{
            bgcolor: "rgba(255,255,255,0.2)",
            width: 32,
            height: 32,
          }}
        >
          {activity
            ? getWorkItemIcon(activity.Type || WorkItemTypeEnum.PhoneCall)
            : null}
        </Avatar>
        <Box minWidth={0}>
          <Typography
            variant="subtitle2"
            fontWeight="600"
            noWrap
            sx={{ color: "inherit" }}
          >
            {activity?.Name || `Aktivitet #${activityId}`}
          </Typography>
          {!minimized && activity && (
            <Box display="flex" alignItems="center" gap={0.5}>
              <Typography variant="caption" sx={{ color: "inherit", opacity: 0.8 }}>
                {getWorkItemTypeName(activity.Type || WorkItemTypeEnum.NotSet)}
                {activity.Customer?.Name ? ` • ${activity.Customer.Name}` : ""}
              </Typography>
              {activity.Customer?.Id && (
                <Tooltip title="Gå till kundprofil">
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/customer/${activity.Customer.Id}`);
                    }}
                    size="small"
                    sx={{
                      color: "white",
                      opacity: 0.8,
                      "&:hover": { opacity: 1, bgcolor: "rgba(255,255,255,0.2)" },
                      p: 0.25,
                    }}
                  >
                    <OpenInNewIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
        </Box>
        {isRefreshing && (
          <CircularProgress size={14} thickness={4} sx={{ color: "white", ml: 1 }} />
        )}
        {isRefreshing && (
          <Refresh
            sx={{
              color: "white",
              fontSize: 14,
              animation: "spin 1s linear infinite",
              "@keyframes spin": {
                "0%": { transform: "rotate(0deg)" },
                "100%": { transform: "rotate(360deg)" },
              },
            }}
          />
        )}
      </Box>
      <Box display="flex" alignItems="center" gap={0.5}>
        {activity && !minimized && (
          <Tooltip
            title={
              activity.FavoriteType === WorkItemFavoriteTypeEnum.Favorite
                ? "Ta bort från favoriter"
                : "Lägg till som favorit"
            }
          >
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                onToggleFavorite();
              }}
              size="small"
              sx={{ color: "white", "&:hover": { bgcolor: "rgba(255,255,255,0.2)" } }}
            >
              {activity.FavoriteType === WorkItemFavoriteTypeEnum.Favorite ? (
                <StarIcon sx={{ fontSize: 16 }} />
              ) : (
                <StarBorderIcon sx={{ fontSize: 16 }} />
              )}
            </IconButton>
          </Tooltip>
        )}
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            onToggleMinimize();
          }}
          size="small"
          sx={{
            color: "white",
            "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
          }}
        >
          {minimized ? <OpenInFull sx={{ fontSize: 16 }} /> : <Minimize sx={{ fontSize: 16 }} />}
        </IconButton>
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          size="small"
          sx={{
            color: "white",
            "&:hover": { bgcolor: "error.main" },
          }}
        >
          <Close sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
    </Box>
  );
};

export default ActivityPanelHeader;
